import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    DataGridBody,
    DataGridRow,
    DataGrid,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridCell,
    TableCellLayout,
    createTableColumn,
    Button,
    Input,
    Field,
  } from "@fluentui/react-components";
import { DeleteRegular, DismissCircleRegular } from '@fluentui/react-icons';

function LicensesPage() {
  const { customerId } = useParams();
  const [customer, setCustomer] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [loadingCustomer, setLoadingCustomer] = useState(true);
  const [formData, setFormData] = useState({
    LicenseID: '',
    StartDate: '',
    ExpiryDate: '',
    Type: '',
  });

  useEffect(() => {
    if (!customerId) return;

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers`)
      .then((res) => res.json())
      .then((data) => {
        const foundCustomer = data.find(c => c.CustomerID === parseInt(customerId, 10));
        if (foundCustomer) {
          setCustomer(foundCustomer);
        }
        setLoadingCustomer(false);
      })
      .catch((err) => {
        console.error('Failed to fetch customer:', err);
        setLoadingCustomer(false);
      });
  }, [customerId]);

  const fetchLicenses = useCallback((customerId) => {
    if (!customerId) {
      setLicenses([]);
      return;
    }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customerId}/licenses`)
      .then((res) => res.json())
      .then((data) => setLicenses(data))
      .catch((err) => {
        console.error('Failed to fetch licenses:', err);
        setLicenses([]);
      });
  }, []);

  useEffect(() => {
    if (customerId) {
      fetchLicenses(customerId);
    } else {
      setLicenses([]);
    }
  }, [customerId, fetchLicenses]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/license-types`)
      .then((res) => res.json())
      .then((data) => setLicenseTypes(data))
      .catch((err) => {
        console.error('Failed to fetch license types:', err);
        setLicenseTypes([]);
      });
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!customerId) return;

    if (!formData.StartDate || !formData.ExpiryDate) {
      alert('Both Start Date and Expiry Date are required.');
      return;
    }

    if (!formData.LicenseID) {
      alert('License Type is required.');
      return;
    }

    const startDate = new Date(formData.StartDate);
    const expiryDate = new Date(formData.ExpiryDate);

    if (expiryDate <= startDate) {
      alert('Expiry Date must be later than Start Date.');
      return;
    }

    const licensePayload = {
      ...formData,
      LicenseID: parseInt(formData.LicenseID, 10),
    };

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customerId}/licenses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(licensePayload),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to add license');
        }
        return res.json();
      })
      .then(() => {
        fetchLicenses(customerId);
        setFormData({
          LicenseID: '',
          StartDate: '',
          ExpiryDate: '',
          Type: '',
        });
      })
      .catch((err) => {
        console.error(err);
        alert('Error adding license.');
      });
  };

  const handleDeactivateLicense = (licenseRecordId) => {
    if (!customerId) return;

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customerId}/licenses/${licenseRecordId}/deactivate`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to deactivate license');
        }
        return res.json();
      })
      .then(() => {
        fetchLicenses(customerId);
      })
      .catch((err) => {
        console.error(err);
        alert('Error deactivating license.');
      });
  };

  const handleRemoveLicense = (licenseRecordId) => {
    if (!customerId) return;

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customerId}/licenses/${licenseRecordId}`, {
      method: 'DELETE',
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to remove license');
        }
        return res.json();
      })
      .then(() => {
        fetchLicenses(customerId);
      })
      .catch((err) => {
        console.error(err);
        alert('Error removing license.');
      });
  };

  const getLicenseDisplayName = (licenseID) => {
    const licenseType = licenseTypes.find((lt) => lt.LicenseID === licenseID);
    return licenseType ? licenseType.DisplayName : 'Unknown License Type';
  };

  const licenseColumns = [
    createTableColumn({
      columnId: 'LicenseID',
      compare: (a, b) => {
        const nameA = getLicenseDisplayName(a.LicenseID);
        const nameB = getLicenseDisplayName(b.LicenseID);
        return nameA.localeCompare(nameB);
      },
      renderHeaderCell: () => 'License Type',
      renderCell: (item) => (
        <TableCellLayout>
          {getLicenseDisplayName(item.LicenseID)}
        </TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'StartDate',
      compare: (a, b) => {
        return (a.StartDate || '').localeCompare(b.StartDate || '');
      },
      renderHeaderCell: () => 'Start Date',
      renderCell: (item) => (
        <TableCellLayout>
          {item.StartDate ? item.StartDate.split('T')[0] : '-'}
        </TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'ExpiryDate',
      compare: (a, b) => {
        return (a.ExpiryDate || '').localeCompare(b.ExpiryDate || '');
      },
      renderHeaderCell: () => 'Expiry Date',
      renderCell: (item) => (
        <TableCellLayout>
          {item.ExpiryDate ? item.ExpiryDate.split('T')[0] : '-'}
        </TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'Type',
      compare: (a, b) => {
        return (a.Type || '').localeCompare(b.Type || '');
      },
      renderHeaderCell: () => 'Type',
      renderCell: (item) => (
        <TableCellLayout>
          {item.Type || '-'}
        </TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'IsActive',
      compare: (a, b) => {
        return (a.IsActive ? 1 : 0) - (b.IsActive ? 1 : 0);
      },
      renderHeaderCell: () => 'Is Active',
      renderCell: (item) => (
        <TableCellLayout>
          {item.IsActive ? 'Yes' : 'No'}
        </TableCellLayout>
      )
    }),
    createTableColumn({
      columnId: 'Actions',
      compare: () => 0,
      renderHeaderCell: () => 'Actions',
      renderCell: (item) => (
        <TableCellLayout>
          <div style={{ display: 'flex', gap: '4px' }}>
            {item.IsActive && (
              <Button
                aria-label="Deactivate"
                className="deactivate-button"
                icon={<DismissCircleRegular />}
                onClick={() => handleDeactivateLicense(item.Id)}
              />
            )}
            <Button
              aria-label="Delete"
              className="delete-button"
              icon={<DeleteRegular />}
              onClick={() => handleRemoveLicense(item.Id)}
            />
          </div>
        </TableCellLayout>
      )
    })
  ];

  const buttonStyles = `
    .delete-button:hover:not(:disabled) {
      background-color: #d13438 !important;
      color: white !important;
      border-color: #d13438 !important;
    }
    .delete-button:hover:not(:disabled) svg {
      color: white !important;
    }
    .deactivate-button:hover:not(:disabled) {
      background-color: #ff8c00 !important;
      color: white !important;
      border-color: #ff8c00 !important;
    }
    .deactivate-button:hover:not(:disabled) svg {
      color: white !important;
    }
    .add-license-button {
      background-color: #107c10 !important;
      color: white !important;
      border-color: #107c10 !important;
      border-radius: 4px !important;
      padding: 6px 12px !important;
      transition: all 0.2s ease !important;
    }
    .add-license-button:hover:not(:disabled) {
      background-color: #0b6e0b !important;
      color: white !important;
      border-color: #0b6e0b !important;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(16, 124, 16, 0.3) !important;
    }
  `;

  return (
    <div style={{ margin: '20px' }}>
      <style>{buttonStyles}</style>
      <h1>Licenses</h1>
      <Link 
        to="/customers" 
        style={{ 
          display: 'inline-block', 
          marginBottom: '20px', 
          padding: '8px 16px', 
          backgroundColor: '#0078d4', 
          color: 'white', 
          textDecoration: 'none', 
          borderRadius: '4px',
          fontSize: '14px'
        }}
      >
        Back to Customers
      </Link>
      {loadingCustomer ? (
        <p>Loading customer information...</p>
      ) : customer ? (
        <>
          <h2>Licenses for {customer.CompanyName}</h2>
          
          <div style={{ 
            border: '1px solid #e1dfdd', 
            borderRadius: '4px',
            padding: '12px',
            marginTop: '16px',
            marginBottom: '16px',
            backgroundColor: '#faf9f8',
            width: '100%',
            maxWidth: '1200px'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '8px', fontSize: '16px' }}>Add New License</h3>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <Field label="License Type" required style={{ minWidth: '270px', maxWidth: '350px' }}>
                  <select
                    value={formData.LicenseID}
                    onChange={(e) => handleChange('LicenseID', e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '4px 8px',
                      border: '1px solid #605e5c',
                      borderRadius: '2px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      backgroundColor: 'white',
                      minHeight: '32px'
                    }}
                  >
                    <option value="">-- Select License Type --</option>
                    {licenseTypes.map((lt) => (
                      <option key={lt.LicenseID} value={lt.LicenseID}>
                        {lt.DisplayName}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Start Date" required style={{ minWidth: '150px' }}>
                  <Input
                    type="date"
                    value={formData.StartDate}
                    onChange={(e) => handleChange('StartDate', e.target.value)}
                    required
                  />
                </Field>

                <Field label="Expiry Date" required style={{ minWidth: '150px' }}>
                  <Input
                    type="date"
                    value={formData.ExpiryDate}
                    onChange={(e) => handleChange('ExpiryDate', e.target.value)}
                    min={formData.StartDate || undefined}
                    required
                  />
                </Field>

                <Field label="Type" style={{ minWidth: '150px' }}>
                  <Input
                    type="text"
                    value={formData.Type}
                    onChange={(e) => handleChange('Type', e.target.value)}
                    placeholder="Enter license type"
                  />
                </Field>

                <Button type="submit" className="add-license-button" style={{ marginBottom: '0' }}>
                  Add License
                </Button>
              </div>
            </form>
          </div>

          {licenses.length === 0 ? (
            <p>No licenses found.</p>
          ) : (
            <div className="no-clickable-rows" style={{ 
              border: '1px solid #e1dfdd', 
              borderRadius: '4px',
              overflow: 'hidden',
              marginTop: '20px'
            }}>
              <DataGrid
                items={licenses}
                columns={licenseColumns}
                sortable
                noNativeElements={false}
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  tableLayout: 'auto'
                }}
              >
                <DataGridHeader>
                  <DataGridRow style={{ borderBottom: '2px solid #e1dfdd' }}>
                    {({ renderHeaderCell }) => (
                      <DataGridHeaderCell
                        style={{
                          borderRight: '1px solid #e1dfdd',
                          padding: '2px 4px',
                          backgroundColor: '#f3f2f1',
                          fontWeight: '600'
                        }}
                      >
                        {renderHeaderCell()}
                      </DataGridHeaderCell>
                    )}
                  </DataGridRow>
                </DataGridHeader>
                <DataGridBody>
                  {({ item, rowId }) => (
                    <DataGridRow 
                      key={rowId}
                      style={{ borderBottom: '1px solid #e1dfdd' }}
                    >
                      {({ renderCell }) => (
                        <DataGridCell
                          style={{
                            borderRight: '1px solid #e1dfdd',
                            padding: '2px 4px'
                          }}
                        >
                          {renderCell(item)}
                        </DataGridCell>
                      )}
                    </DataGridRow>
                  )}
                </DataGridBody>
              </DataGrid>
            </div>
          )}
        </>
      ) : (
        <p>Customer not found.</p>
      )}
    </div>
  );
}

export default LicensesPage;
